'use client';

import { useEffect, useEffectEvent, useRef, useState } from 'react';

interface UseInactivityTimeoutOptions {
  isRecording: boolean;
  isPaused?: boolean;
  getRemainingTime: () => number | null;
  isTimedOut: () => boolean;
  pauseRecording: (reason?: string) => void;
  resumeRecording: (reason?: string) => void;
  updateActivity: () => void;
}

export function useInactivityTimeout({
  isRecording,
  isPaused = false,
  getRemainingTime,
  isTimedOut,
  pauseRecording,
  resumeRecording,
  updateActivity,
}: UseInactivityTimeoutOptions) {
  const resumeRecordingRef = useRef(resumeRecording);
  const updateActivityRef = useRef(updateActivity);

  const [currentRemainingTime, setCurrentRemainingTime] = useState<
    number | null
  >(null);
  const [isPausedByTimeout, setIsPausedByTimeout] = useState(false);
  const [showTimeoutOverlay, setShowTimeoutOverlay] = useState(false);
  const [lastIsActive, setLastIsActive] = useState(
    isRecording && !isPausedByTimeout && !isPaused
  );

  const onIsTimedOut = useEffectEvent(() => isTimedOut());
  const onActivity = useEffectEvent(() => updateActivity());
  const onPauseRecording = useEffectEvent(() => pauseRecording('timeout'));
  const onGetRemainingTime = useEffectEvent(() => getRemainingTime());

  const isActive = isRecording && !isPausedByTimeout && !isPaused;

  if (lastIsActive !== isActive) {
    setLastIsActive(isActive);
    if (!isActive) setCurrentRemainingTime(null);
  }

  useEffect(() => {
    if (!isActive) return;

    const checkTimeoutAndUpdateTime = () => {
      setCurrentRemainingTime(onGetRemainingTime());

      if (onIsTimedOut() && !showTimeoutOverlay) {
        onPauseRecording();
        setIsPausedByTimeout(true);
        setShowTimeoutOverlay(true);
      }
    };

    const initialTimeout = setTimeout(checkTimeoutAndUpdateTime, 0);
    const interval = setInterval(checkTimeoutAndUpdateTime, 1000);
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [isActive, showTimeoutOverlay]);

  useEffect(() => {
    if (!isRecording || isPausedByTimeout || isPaused) return;

    const handleActivity = () => {
      onActivity();
    };

    const events = [
      'mousemove',
      'mousedown',
      'keypress',
      'scroll',
      'touchstart',
    ];
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [isRecording, isPausedByTimeout, isPaused]);

  const handleResumeRecording = () => {
    setShowTimeoutOverlay(false);
    setIsPausedByTimeout(false);
    updateActivityRef.current();
    resumeRecordingRef.current('timeout');
  };

  useEffect(() => {
    resumeRecordingRef.current = resumeRecording;
    updateActivityRef.current = updateActivity;
  });

  return {
    isPausedByTimeout,
    showTimeoutOverlay,
    currentRemainingTime,
    handleResumeRecording,
  };
}
